import { commandCenterPageConfigApi } from 'app/services/api';

import { getDefaultErrorMessage } from 'utils/axios';
import { createNamedActions } from 'utils/namedActionTypesProxy';

export const COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE = createNamedActions(
  'commandCenterPageConfig',
  {
    LOAD_CONFIG_REQUEST: 'LOAD_CONFIG_REQUEST',
    LOAD_CONFIG_SUCCESS: 'LOAD_CONFIG_SUCCESS',
    LOAD_CONFIG_FAILURE: 'LOAD_CONFIG_FAILURE',

    UPDATE_CONFIG_REQUEST: 'UPDATE_CONFIG_REQUEST',
    UPDATE_CONFIG_SUCCESS: 'UPDATE_CONFIG_SUCCESS',
    UPDATE_CONFIG_FAILURE: 'UPDATE_CONFIG_FAILURE',

    LOAD_SUB_COMPANY_CONFIG_REQUEST: 'LOAD_SUB_COMPANY_CONFIG_REQUEST',
    LOAD_SUB_COMPANY_CONFIG_SUCCESS: 'LOAD_SUB_COMPANY_CONFIG_SUCCESS',
    LOAD_SUB_COMPANY_CONFIG_FAILURE: 'LOAD_SUB_COMPANY_CONFIG_FAILURE',

    UPDATE_SUB_COMPANY_CONFIG_REQUEST: 'UPDATE_SUB_COMPANY_CONFIG_REQUEST',
    UPDATE_SUB_COMPANY_CONFIG_SUCCESS: 'UPDATE_SUB_COMPANY_CONFIG_SUCCESS',
    UPDATE_SUB_COMPANY_CONFIG_FAILURE: 'UPDATE_SUB_COMPANY_CONFIG_FAILURE',

    // DEV-4136: "All subcompanies (default)" scope — ConfigScope=AllSubCompanies.
    LOAD_ALL_SUBCOMPANIES_CONFIG_REQUEST: 'LOAD_ALL_SUBCOMPANIES_CONFIG_REQUEST',
    LOAD_ALL_SUBCOMPANIES_CONFIG_SUCCESS: 'LOAD_ALL_SUBCOMPANIES_CONFIG_SUCCESS',
    LOAD_ALL_SUBCOMPANIES_CONFIG_FAILURE: 'LOAD_ALL_SUBCOMPANIES_CONFIG_FAILURE',

    UPDATE_ALL_SUBCOMPANIES_CONFIG_REQUEST: 'UPDATE_ALL_SUBCOMPANIES_CONFIG_REQUEST',
    UPDATE_ALL_SUBCOMPANIES_CONFIG_SUCCESS: 'UPDATE_ALL_SUBCOMPANIES_CONFIG_SUCCESS',
    UPDATE_ALL_SUBCOMPANIES_CONFIG_FAILURE: 'UPDATE_ALL_SUBCOMPANIES_CONFIG_FAILURE',

    RESET_STATE: 'RESET_STATE',
  },
);

// Owns its own {loading, error, data} branch — the page falls back to the in-code
// default config when data is null, so a failure degrades gracefully rather than
// routing through the global error toast.
export const loadCommandCenterPageConfigAction =
  ({ companyId }) =>
  async dispatch => {
    dispatch({ type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.LOAD_CONFIG_REQUEST });

    try {
      const { data } = await commandCenterPageConfigApi.getCommandCenterPageConfig({ companyId });

      return dispatch({
        type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.LOAD_CONFIG_SUCCESS,
        payload: data ?? null,
      });
    } catch (error) {
      return dispatch({
        type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.LOAD_CONFIG_FAILURE,
        payload: getDefaultErrorMessage(error),
      });
    }
  };

// Persists the edited layout for a company (stub-backed today — the api mock
// keeps an in-memory store keyed by companyId). On success the saved blob becomes
// the new source of truth so the live broker homepage reflects the admin's edits.
export const updateCommandCenterPageConfigAction =
  ({ companyId, config }) =>
  async dispatch => {
    dispatch({ type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.UPDATE_CONFIG_REQUEST });

    try {
      const { data } = await commandCenterPageConfigApi.updateCommandCenterPageConfig({
        companyId,
        config,
      });

      return dispatch({
        type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.UPDATE_CONFIG_SUCCESS,
        payload: data ?? config,
      });
    } catch (error) {
      return dispatch({
        type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.UPDATE_CONFIG_FAILURE,
        payload: getDefaultErrorMessage(error),
      });
    }
  };

// DEV-4136: load config for a specific subcompany scope. The backend endpoint
// ships in a separate PR; 404 resolves to null (inherits parent config at runtime).
export const loadSubCompanyCommandCenterPageConfigAction =
  ({ companyId, subCompanyId }) =>
  async dispatch => {
    dispatch({ type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.LOAD_SUB_COMPANY_CONFIG_REQUEST });

    try {
      const { data } = await commandCenterPageConfigApi.getSubCompanyCommandCenterPageConfig({
        companyId,
        subCompanyId,
      });

      return dispatch({
        type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.LOAD_SUB_COMPANY_CONFIG_SUCCESS,
        payload: data ?? null,
      });
    } catch (error) {
      dispatch({
        type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.LOAD_SUB_COMPANY_CONFIG_FAILURE,
        payload: getDefaultErrorMessage(error),
      });

      throw error;
    }
  };

export const updateSubCompanyCommandCenterPageConfigAction =
  ({ companyId, subCompanyId, config }) =>
  async dispatch => {
    dispatch({ type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.UPDATE_SUB_COMPANY_CONFIG_REQUEST });

    try {
      const { data } = await commandCenterPageConfigApi.updateSubCompanyCommandCenterPageConfig({
        companyId,
        subCompanyId,
        config,
      });

      return dispatch({
        type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.UPDATE_SUB_COMPANY_CONFIG_SUCCESS,
        payload: data ?? config,
      });
    } catch (error) {
      dispatch({
        type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.UPDATE_SUB_COMPANY_CONFIG_FAILURE,
        payload: getDefaultErrorMessage(error),
      });

      throw error;
    }
  };

// DEV-4136: load config for the "all subcompanies default" scope. The backend
// endpoint ships in a separate Accounts PR; 404 re-throws so the page can show the pending
// banner (same pattern as loadSubCompanyCommandCenterPageConfigAction).
export const loadAllSubCompaniesCommandCenterPageConfigAction =
  ({ companyId }) =>
  async dispatch => {
    dispatch({
      type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.LOAD_ALL_SUBCOMPANIES_CONFIG_REQUEST,
    });

    try {
      const { data } = await commandCenterPageConfigApi.getAllSubCompaniesCommandCenterPageConfig({
        companyId,
      });

      return dispatch({
        type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.LOAD_ALL_SUBCOMPANIES_CONFIG_SUCCESS,
        payload: data ?? null,
      });
    } catch (error) {
      dispatch({
        type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.LOAD_ALL_SUBCOMPANIES_CONFIG_FAILURE,
        payload: getDefaultErrorMessage(error),
      });

      throw error;
    }
  };

export const updateAllSubCompaniesCommandCenterPageConfigAction =
  ({ companyId, config }) =>
  async dispatch => {
    dispatch({
      type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.UPDATE_ALL_SUBCOMPANIES_CONFIG_REQUEST,
    });

    try {
      const { data } =
        await commandCenterPageConfigApi.updateAllSubCompaniesCommandCenterPageConfig({
          companyId,
          config,
        });

      return dispatch({
        type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.UPDATE_ALL_SUBCOMPANIES_CONFIG_SUCCESS,
        payload: data ?? config,
      });
    } catch (error) {
      dispatch({
        type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.UPDATE_ALL_SUBCOMPANIES_CONFIG_FAILURE,
        payload: getDefaultErrorMessage(error),
      });

      throw error;
    }
  };

export const resetCommandCenterPageConfigAction = () => ({
  type: COMMAND_CENTER_PAGE_CONFIG_ACTION_TYPE.RESET_STATE,
});
